import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';

import PortalHeader from '../../components/Portal/PortalHeader';
import {
	fetchWorkshopsForAdmin,
	fetchWorkshopEnrollments,
	fetchWorkshopLeads,
	fetchWorkshopWaitlist,
} from '../../utils/workshopService';
import { csvEscapeCell } from '../../utils/csv';
import './WorkshopEnrollments.css';

// ── helpers ──
function tsToMs(ts) {
	if (!ts) return 0;
	if (typeof ts.toDate === 'function') return ts.toDate().getTime();
	if (ts.seconds) return ts.seconds * 1000;
	if (ts._seconds) return ts._seconds * 1000;
	return 0;
}
function fmtDate(ts) {
	const ms = tsToMs(ts);
	return ms
		? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		: '—';
}

// Maps to the .ps-badge--* modifiers in portalSurface.css. Replaced Bootstrap's
// `bg` variants on conversion; 'secondary' had no house equivalent and becomes
// 'neutral', which is the same visual role.
const STATUS_TONE = {
	confirmed: 'success',
	pending: 'warning',
	abandoned: 'neutral',
	refunded: 'danger',
	waitlist: 'info',
};

const MEDIA_CONSENT_LABEL = {
	private: 'Record — private to family only',
	public: 'Record — OK to share publicly',
	none: 'Do not record',
};
const mediaConsentLabel = (v) => (v ? MEDIA_CONSENT_LABEL[v] || v : '');

function buildRows(enrollments, leads, waitlist) {
	const rows = [];
	for (const e of enrollments) {
		rows.push({
			id: e.id,
			kind: 'enrollment',
			workshopId: e.workshopId,
			workshopTitle: e.workshopTitle,
			groupLabel: e.groupLabel,
			studentName: e.student?.name,
			studentAge: e.student?.age,
			parentName: e.parent?.name,
			parentEmail: e.parent?.email,
			parentPhone: e.parent?.phone,
			emergencyName: e.emergencyContact?.name,
			emergencyPhone: e.emergencyContact?.phone,
			amount: e.amountPaid,
			status: e.status === 'refunded' ? 'refunded' : 'confirmed',
			medicalNotes: e.options?.medicalNotes,
			priorExperience: e.options?.priorExperience,
			interestedInFall: !!e.options?.interestedInFallLessons,
			mediaConsent: e.options?.mediaConsent || null,
			createdAt: e.createdAt,
			stripeRef: e.stripePaymentIntentId,
		});
	}
	for (const l of leads) {
		if (l.status === 'converted') continue; // appears as an enrollment instead
		rows.push({
			id: l.id,
			kind: 'lead',
			workshopId: l.workshopId,
			workshopTitle: l.workshopTitle,
			groupLabel: l.groupLabel,
			studentName: l.student?.name,
			studentAge: l.student?.age,
			parentName: l.parent?.name,
			parentEmail: l.parent?.email,
			parentPhone: l.parent?.phone,
			emergencyName: l.emergencyContact?.name,
			emergencyPhone: l.emergencyContact?.phone,
			amount: l.amount,
			status: l.status === 'pending_payment' ? 'pending' : 'abandoned',
			medicalNotes: l.options?.medicalNotes,
			priorExperience: l.options?.priorExperience,
			interestedInFall: !!l.options?.interestedInFallLessons,
			mediaConsent: l.options?.mediaConsent || null,
			createdAt: l.createdAt,
			stripeRef: l.stripeSessionId,
		});
	}
	for (const w of waitlist) {
		rows.push({
			id: w.id,
			kind: 'waitlist',
			workshopId: w.workshopId,
			workshopTitle: w.workshopTitle,
			groupId: w.groupId,
			groupLabel: w.groupLabel,
			studentName: w.student?.name,
			studentAge: w.student?.age,
			parentName: w.parent?.name,
			parentEmail: w.parent?.email,
			parentPhone: w.parent?.phone,
			note: w.note || null,
			amount: null,
			status: 'waitlist',
			createdAt: w.createdAt,
		});
	}
	rows.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
	return rows;
}

function toCsv(rows) {
	const headers = [
		'Workshop', 'Session', 'Status', 'Student', 'Age', 'Parent', 'Email', 'Phone',
		'Emergency Name', 'Emergency Phone', 'Amount', 'Medical Notes',
		'Prior Experience', 'Media Consent', 'Fall Interest', 'Note', 'Signed Up',
	];
	const lines = [headers.join(',')];
	for (const r of rows) {
		lines.push(
			[
				r.workshopTitle, r.groupLabel, r.status, r.studentName, r.studentAge,
				r.parentName, r.parentEmail, r.parentPhone, r.emergencyName, r.emergencyPhone,
				r.amount != null ? `$${r.amount}` : '', r.medicalNotes, r.priorExperience,
				mediaConsentLabel(r.mediaConsent),
				r.interestedInFall ? 'Yes' : 'No', r.note, fmtDate(r.createdAt),
			].map(csvEscapeCell).join(',')
		);
	}
	return lines.join('\n');
}

const WorkshopEnrollments = () => {
	const [workshops, setWorkshops] = useState([]);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	const [workshopFilter, setWorkshopFilter] = useState('all');
	const [statusFilter, setStatusFilter] = useState('all');
	const [search, setSearch] = useState('');
	const [expandedId, setExpandedId] = useState(null);

	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const [ws, enrollments, leads, waitlist] = await Promise.all([
					fetchWorkshopsForAdmin(),
					fetchWorkshopEnrollments(),
					fetchWorkshopLeads(),
					fetchWorkshopWaitlist(),
				]);
				if (!active) return;
				setWorkshops(ws);
				setRows(buildRows(enrollments, leads, waitlist));
			} catch (err) {
				console.error('Failed to load workshop registrations:', err);
				if (active) setError('Could not load registrations.');
			} finally {
				if (active) setLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	const filteredRows = useMemo(() => {
		const term = search.trim().toLowerCase();
		return rows.filter((r) => {
			if (workshopFilter !== 'all' && r.workshopId !== workshopFilter) return false;
			if (statusFilter !== 'all' && r.status !== statusFilter) return false;
			if (term) {
				const hay = `${r.studentName} ${r.parentName} ${r.parentEmail}`.toLowerCase();
				if (!hay.includes(term)) return false;
			}
			return true;
		});
	}, [rows, workshopFilter, statusFilter, search]);

	// Waiting-list counts keyed by `${workshopId}|${groupId}`, for the cards.
	const waitlistCounts = useMemo(() => {
		const counts = {};
		for (const r of rows) {
			if (r.kind !== 'waitlist') continue;
			const key = `${r.workshopId}|${r.groupId}`;
			counts[key] = (counts[key] || 0) + 1;
		}
		return counts;
	}, [rows]);

	const handleExport = () => {
		const csv = toCsv(filteredRows);
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		const label =
			workshopFilter === 'all'
				? 'all-workshops'
				: workshops.find((w) => w.id === workshopFilter)?.slug || workshopFilter;
		a.href = url;
		a.download = `workshop-registrations-${label}.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const confirmedCount = filteredRows.filter((r) => r.status === 'confirmed').length;

	return (
		<>
			<PortalHeader
				title='Workshop Registrations'
				subtitle='Summer 2026 workshop signups, capacity, and rosters.'
			/>
			<div className='ps-page workshop-enrollments'>
				{loading && <div className='ps-spinner' role='status' aria-label='Loading' />}

				{!loading && error && <p className='ps-alert ps-alert--danger'>{error}</p>}

				{!loading && !error && (
					<>
						{/* Capacity summary */}
						<div className='we-summary-grid'>
							{workshops.map((w) => (
								<div key={w.id}>
									<wa-card appearance='outlined' class='we-summary-card h-100'>
										<div className='we-summary-title'>{w.title}</div>
										<div className='we-summary-status'>{w.status}</div>
										{(w.groups || []).map((g) => {
											const enrolled = g.enrolledCount || 0;
											const held = g.heldCount || 0;
											const waiting = waitlistCounts[`${w.id}|${g.id}`] || 0;
											return (
												<div key={g.id} className='we-summary-group'>
													<span>{w.requiresGroupSelection ? g.label : 'Session'}</span>
													<span className='we-summary-count'>
														{enrolled}/{g.capacity}
														{held > 0 ? ` (+${held} pending)` : ''}
														{waiting > 0 ? ` · ${waiting} waiting` : ''}
													</span>
												</div>
											);
										})}
									</wa-card>
								</div>
							))}
						</div>

						{/* Filters */}
						<div className='ps-toolbar'>
							<div className='ps-field we-filter'>
								<label className='ps-label' htmlFor='we-workshop'>Workshop</label>
								<select
									id='we-workshop'
									className='ps-select'
									value={workshopFilter}
									onChange={(e) => setWorkshopFilter(e.target.value)}>
									<option value='all'>All workshops</option>
									{workshops.map((w) => (
										<option key={w.id} value={w.id}>{w.title}</option>
									))}
								</select>
							</div>
							<div className='ps-field we-filter'>
								<label className='ps-label' htmlFor='we-status'>Status</label>
								<select
									id='we-status'
									className='ps-select'
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}>
									<option value='all'>All statuses</option>
									<option value='confirmed'>Confirmed</option>
									<option value='pending'>Pending</option>
									<option value='abandoned'>Abandoned</option>
									<option value='refunded'>Refunded</option>
									<option value='waitlist'>Waitlist</option>
								</select>
							</div>
							<div className='ps-field we-filter we-filter--search'>
								<label className='ps-label' htmlFor='we-search'>Search</label>
								<input
									id='we-search'
									type='search'
									className='ps-input'
									placeholder='Student, parent, or email'
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</div>
							<div className='we-toolbar-actions'>
								<wa-button
									appearance='outlined'
									variant='brand'
									size='medium'
									onClick={handleExport}>
									Export CSV
								</wa-button>
							</div>
						</div>

						<p className='we-count'>
							{filteredRows.length} registration{filteredRows.length === 1 ? '' : 's'}
							{confirmedCount !== filteredRows.length ? ` · ${confirmedCount} confirmed` : ''}
						</p>

						{/* Roster */}
						<div className='ps-table-wrap'>
							<table className='ps-table we-table'>
								<thead>
									<tr>
										<th>Student</th>
										<th>Age</th>
										<th>Parent</th>
										<th>Contact</th>
										<th>Workshop · Session</th>
										<th>Amount</th>
										<th>Status</th>
										<th>Signed up</th>
									</tr>
								</thead>
								<tbody>
									{filteredRows.length === 0 && (
										<tr>
											<td colSpan={8}>
												<div className='ps-empty'>No registrations match these filters.</div>
											</td>
										</tr>
									)}
									{filteredRows.map((r) => (
										<React.Fragment key={r.id}>
											<tr
												className='we-row'
												onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
												<td>
													{r.studentName}{' '}
													{r.medicalNotes && (
														<FontAwesomeIcon
															icon={faTriangleExclamation}
															className='text-warning'
															title='Has medical notes / allergies'
														/>
													)}
												</td>
												<td>{r.studentAge}</td>
												<td>{r.parentName}</td>
												<td>
													<div>{r.parentEmail}</div>
													<div className='we-subtle'>{r.parentPhone}</div>
												</td>
												<td>
													{r.workshopTitle}
													{r.groupLabel ? <div className='we-subtle'>{r.groupLabel}</div> : null}
												</td>
												<td>{r.amount != null ? `$${r.amount}` : '—'}</td>
												<td>
													<span className={`ps-badge ps-badge--${STATUS_TONE[r.status] || 'neutral'}`}>
														{r.status}
													</span>
												</td>
												<td>{fmtDate(r.createdAt)}</td>
											</tr>
											{expandedId === r.id && (
												<tr className='we-detail-row'>
													<td colSpan={8}>
														<div className='we-detail'>
															<div>
																<strong>Emergency contact:</strong>{' '}
																{r.emergencyName || '—'} · {r.emergencyPhone || '—'}
															</div>
															<div>
																<strong>Medical / allergies:</strong>{' '}
																{r.medicalNotes || '—'}
															</div>
															<div>
																<strong>Prior experience:</strong>{' '}
																{r.priorExperience || '—'}
															</div>
															{r.mediaConsent && (
																<div>
																	<strong>Media consent:</strong>{' '}
																	{mediaConsentLabel(r.mediaConsent)}
																</div>
															)}
															{r.note && (
																<div>
																	<strong>Note:</strong> {r.note}
																</div>
															)}
															<div>
																<strong>Interested in fall lessons:</strong>{' '}
																{r.interestedInFall ? 'Yes' : 'No'}
															</div>
															<div className='we-subtle'>
																{r.kind} · ref {r.stripeRef || '—'}
															</div>
														</div>
													</td>
												</tr>
											)}
										</React.Fragment>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default WorkshopEnrollments;
